import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { adminService } from '../../lib/api';

const ConformiteTab = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('audit'); // audit, alertes
  const [filterAction, setFilterAction] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAuditLogs();
      setAuditLogs(data.logs || []);
    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertes = async () => {
    try {
      setLoading(true);
      const data = await adminService.getComplianceAlerts();
      setAlertes(data.alertes || []);
    } catch (error) {
      console.error('Erreur lors du chargement des alertes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === 'alertes' && alertes.length === 0) {
      fetchAlertes();
    }
  };

  const getActionBadgeColor = (action) => {
    const colors = {
      'CREATION_ENTREPRISE': 'bg-blue-100 text-blue-800',
      'SUSPENSION_ENTREPRISE': 'bg-orange-100 text-orange-800',
      'REACTIVATION_ENTREPRISE': 'bg-green-100 text-green-800',
      'VALIDATION_ENTREPRISE': 'bg-purple-100 text-purple-800',
      'MODIFICATION_PARAMETRES': 'bg-yellow-100 text-yellow-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const getSeverityBadge = (severite) => {
    const config = {
      'HAUTE': { color: 'bg-red-100 text-red-800', icon: '🔴' },
      'MOYENNE': { color: 'bg-orange-100 text-orange-800', icon: '🟠' },
      'FAIBLE': { color: 'bg-yellow-100 text-yellow-800', icon: '🟡' }
    };
    return config[severite] || { color: 'bg-gray-100 text-gray-800', icon: '⚪' };
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      'SOLDE_NEGATIF': 'Solde Négatif Important',
      'AVANCES_MULTIPLES': 'Avances Multiples Même Jour',
      'MONTANT_ELEVE': 'Montant Inhabituel',
      'FREQUENCE_ELEVEE': 'Fréquence Anormale'
    };
    return labels[type] || type;
  };

  const filteredLogs = filterAction 
    ? auditLogs.filter(log => log.action === filterAction)
    : auditLogs;

  const uniqueActions = [...new Set(auditLogs.map(log => log.action))];

  if (loading && auditLogs.length === 0 && alertes.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Chargement des données de conformité...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Navigation des vues */}
      <Card>
        <CardContent className="p-4">
          <div className="flex space-x-4">
            <Button
              variant={activeView === 'audit' ? 'default' : 'outline'}
              onClick={() => handleViewChange('audit')}
            >
              📋 Journal d'Audit
            </Button>
            <Button
              variant={activeView === 'alertes' ? 'default' : 'outline'}
              onClick={() => handleViewChange('alertes')}
            >
              🚨 Alertes de Conformité
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Vue Journal d'Audit */}
      {activeView === 'audit' && (
        <div className="space-y-6">
          {/* En-tête avec filtres */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Journal d'Audit ({filteredLogs.length} entrées)</CardTitle>
                <div className="flex items-center space-x-3">
                  <select
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Toutes les actions</option>
                    {uniqueActions.map(action => (
                      <option key={action} value={action}>{action.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <Button size="sm" variant="outline" onClick={fetchAuditLogs}>
                    🔄 Actualiser
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredLogs.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📋</div>
                  <p className="text-gray-600">Aucune entrée d'audit disponible</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredLogs.map((log) => (
                    <div key={log.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getActionBadgeColor(log.action)}`}>
                              {log.action.replace(/_/g, ' ')}
                            </span>
                            <span className="text-sm text-gray-500">{formatDate(log.timestamp)}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-700">Entité</p>
                              <p className="text-sm text-gray-900">{log.entite_nom}</p>
                              <p className="text-xs text-gray-500">{log.entite_type} - {log.entite_id.slice(0, 8)}...</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-700">Administrateur</p>
                              <p className="text-sm text-gray-900">{log.administrateur}</p>
                            </div>
                          </div>
                          <div className="mt-2">
                            <p className="text-sm text-gray-600">{log.details}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistiques d'audit */}
          <Card>
            <CardHeader>
              <CardTitle>Statistiques du Journal d'Audit</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Total Entrées</p>
                  <p className="text-2xl font-bold text-blue-700">{auditLogs.length}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">Créations</p>
                  <p className="text-2xl font-bold text-green-700">
                    {auditLogs.filter(l => l.action.includes('CREATION')).length}
                  </p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-600 font-medium">Suspensions</p>
                  <p className="text-2xl font-bold text-orange-700">
                    {auditLogs.filter(l => l.action.includes('SUSPENSION')).length}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600 font-medium">Modifications</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {auditLogs.filter(l => l.action.includes('MODIFICATION')).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Vue Alertes de Conformité */}
      {activeView === 'alertes' && (
        <div className="space-y-6">
          {/* Statistiques des alertes */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-sm font-medium">Alertes Haute</p>
                    <p className="text-3xl font-bold">
                      {alertes.filter(a => a.severite === 'HAUTE').length}
                    </p>
                  </div>
                  <div className="text-4xl opacity-80">🔴</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Alertes Moyenne</p>
                    <p className="text-3xl font-bold">
                      {alertes.filter(a => a.severite === 'MOYENNE').length}
                    </p>
                  </div>
                  <div className="text-4xl opacity-80">🟠</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-100 text-sm font-medium">Alertes Faible</p>
                    <p className="text-3xl font-bold">
                      {alertes.filter(a => a.severite === 'FAIBLE').length}
                    </p>
                  </div>
                  <div className="text-4xl opacity-80">🟡</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total Alertes</p>
                    <p className="text-3xl font-bold">{alertes.length}</p>
                  </div>
                  <div className="text-4xl opacity-80">🚨</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Liste des alertes */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Alertes de Conformité Actives</CardTitle>
                <Button size="sm" variant="outline" onClick={fetchAlertes}>
                  🔄 Actualiser
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Chargement des alertes...</p>
                </div>
              ) : alertes.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">✅</div>
                  <p className="text-gray-600">Aucune alerte de conformité détectée</p>
                  <p className="text-sm text-gray-500 mt-2">Toutes les activités sont normales</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {alertes.map((alerte) => {
                    const severityConfig = getSeverityBadge(alerte.severite);
                    return (
                      <div 
                        key={alerte.id} 
                        className={`p-5 border-l-4 rounded-lg ${
                          alerte.severite === 'HAUTE' ? 'border-l-red-500 bg-red-50' :
                          alerte.severite === 'MOYENNE' ? 'border-l-orange-500 bg-orange-50' :
                          'border-l-yellow-500 bg-yellow-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <span className="text-2xl">{severityConfig.icon}</span>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${severityConfig.color}`}>
                                {alerte.severite}
                              </span>
                              <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-gray-700">
                                {getAlertTypeLabel(alerte.type)}
                              </span>
                              <span className="text-sm text-gray-500">{formatDate(alerte.date)}</span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                              <div>
                                <p className="text-sm font-medium text-gray-700">Entité Concernée</p>
                                <p className="text-base font-semibold text-gray-900">{alerte.entite_nom}</p>
                                <p className="text-xs text-gray-500">{alerte.entite_type}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">Description</p>
                                <p className="text-sm text-gray-900">{alerte.description}</p>
                              </div>
                            </div>

                            <div className="p-3 bg-white rounded-lg border border-gray-200">
                              <p className="text-sm font-medium text-blue-700 mb-1">
                                💡 Action Recommandée
                              </p>
                              <p className="text-sm text-gray-700">{alerte.action_recommandee}</p>
                            </div>
                          </div>
                          
                          <div className="ml-4">
                            <Button 
                              size="sm" 
                              className={
                                alerte.severite === 'HAUTE' ? 'bg-red-600 hover:bg-red-700' :
                                alerte.severite === 'MOYENNE' ? 'bg-orange-600 hover:bg-orange-700' :
                                'bg-yellow-600 hover:bg-yellow-700'
                              }
                            >
                              Traiter
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Informations supplémentaires */}
          {alertes.length > 0 && (
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-start">
                  <div className="text-3xl mr-4">ℹ️</div>
                  <div>
                    <h3 className="font-medium text-blue-900 mb-2">À propos des Alertes de Conformité</h3>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>Haute Sévérité</strong> : Nécessite une action immédiate (soldes négatifs importants, activités suspectes)</li>
                      <li>• <strong>Moyenne Sévérité</strong> : À surveiller de près (avances multiples, montants inhabituels)</li>
                      <li>• <strong>Faible Sévérité</strong> : Informationnel, aucune action urgente requise</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ConformiteTab;
